import {
  MaterialsMapping,
  ProgramsMapping,
  ProgramStudent,
  RegistrationMaterial,
  studentGroupInfo,
  StudentProfileInfo,
  CurriculumMaterial,
  RegistrationWrapper,
  RegistrationProgramMaterials,
} from '@interfaces/Student'
import {
  DATE_FORMAT,
  ELECTIVE,
  FINISHED_PROGRAM_TYPES,
  FLEX_LEVEL_STATUS,
  FLEX_VERSION,
  MY_BERLITZ_ENV,
  NOLPID,
  ON_DEMAND,
  PROGRAM_TYPES,
  REG_STATUS,
  VALID_PROGRAM_TYPES,
} from '@utils/constants'
import { IsFlex, getStoredMaterials, getStoredRegistrations, getStoredPrograms } from '@utils/helpers/material'
import { getPermissionFromStorage } from '@utils/helpers/permissions'
import { flattenDeep, groupBy, last, values, orderBy, sortBy, uniqBy } from 'lodash'
import moment from 'moment'
import { getCMID } from '@components/RoleBasedView/features/cr8'
import { CURRICULUM_TEST_STATUS } from '@utils/constants'
import PortalProgram from './Program'
import PortalPrograms from './Programs'
import OnlinePlacementTest from './OnlinePlacemenTest'
import { decodeDDUniqueKey, extractNumbeFromStr, moveToFirstPosition } from '@utils/helpers'
import uniq from 'lodash/uniq'

class PortalStudentProfile {
  student: StudentProfileInfo
  Materials: MaterialsMapping
  RegistrationProgramMaterials: RegistrationProgramMaterials
  DropDownMaterials: MaterialsMapping
  Programs: ProgramsMapping
  ElectivesFlag?: boolean
  EventsBoDFlag?: boolean
  MyBerlitzTutorTabFlag?: boolean
  SkillsTutorTabFlag?: boolean
  AtRiskProgram?: PortalProgram
  MaterialsForDD?: MaterialsMapping
  MaterialsForDD2?: MaterialsMapping
  CT?: CurriculumMaterial[]
  hasMaterials?: boolean
  OPT?: OnlinePlacementTest
  constructor(student: StudentProfileInfo, activeMaterial?: string) {
    this.student = student
    this.ElectivesFlag = getPermissionFromStorage('flag::GERMAN5-8-feature')
    this.EventsBoDFlag = getPermissionFromStorage('flag::Events-BoD')
    // industry specific content flag
    this.MyBerlitzTutorTabFlag = getPermissionFromStorage('flag::industrySpecificContent')
    // skills tutor content flag
    this.SkillsTutorTabFlag = getPermissionFromStorage('flag::skillTutorContent')
    this.MaterialsForDD = this.buildDropdown2()
    this.MaterialsForDD2 = this.buildDropdown3()
    this.CT = student?.RegistrationWrappers?.reduce((acc, current) => {
      const regMaterials = current?.RegistrationInfo?.Materials
      const coreMaterial =
        regMaterials.length > 1
          ? regMaterials.find((material) => !material.IsElective && !material.IsTest)
          : regMaterials[0]
      const MaterialsWithCoreLP: CurriculumMaterial[] = regMaterials
        .filter((material) => material.IsTest)
        .map((material) => ({
          ...material,
          CoreLP: coreMaterial?.LPID ?? '',
        }))
      acc = acc.concat(...MaterialsWithCoreLP)
      return acc
    }, [] as CurriculumMaterial[])
    /**
     * There are scenarios that Registration has no materials []
     * We need to check if that registration is an OPT
     * if it is then we need to add a placeholder material
     */
    const registrationWrappers = (student?.RegistrationWrappers ?? []).map((registration) =>
      OnlinePlacementTest.generatePlaceholder(registration)
    )
    this.RegistrationProgramMaterials = registrationWrappers?.reduce((rAcc, rCurrent) => {
      const currRegistrationId = rCurrent?.RegistrationInfo?.RegistrationId
      const programs = rCurrent?.RegistrationInfo?.Programs?.reduce((pAcc, pCurrent) => {
        const isFlex = IsFlex(rCurrent.RegistrationInfo)
        if (rCurrent.RegistrationInfo.AtRisk && !this.AtRiskProgram) {
          this.AtRiskProgram = new PortalProgram(
            rCurrent.RegistrationInfo.Programs.filter((program) => !FINISHED_PROGRAM_TYPES.includes(program.Status))[0]
          )
        }
        const materials = rCurrent?.RegistrationInfo?.Materials?.reduce((mAcc, curr) => {
          const res = {}
          const levelRegEx = /[0-9]{2}|[0-9]/ // material language level is currently derived from material name
          const RegistrationReferences = student?.RegistrationWrappers.filter(({ RegistrationInfo }) =>
            RegistrationInfo.Materials.some(({ LPID }) => LPID === curr.LPID)
          ).map(({ RegistrationInfo }) => RegistrationInfo)
          res[curr.LPID] = {
            ...curr,
            regStatus: rCurrent?.RegistrationInfo.Status,
            ProgramStartDate: rCurrent?.RegistrationInfo.Programs.map((P) => P.ProgramEnrollDate)[0],
            ProgramEndDate: rCurrent?.RegistrationInfo.ProgramEndDate,
            TestStatus: rCurrent?.RegistrationInfo?.TestStatus,
            partialName: curr.MaterialName?.split(levelRegEx)?.[0],
            languageLevel: rCurrent?.RegistrationInfo.LanguageLevel,
            language: rCurrent?.RegistrationInfo?.Language,
            ProgramType: rCurrent?.RegistrationInfo?.ProgramType ?? '',
            ProgramId: rCurrent?.RegistrationInfo?.Programs?.[0]?.ProgramId, // assumes 1 program per registration
            FlexVersion: rCurrent?.RegistrationInfo?.FlexVersion,
            FlexLevelStatus: rCurrent?.RegistrationInfo?.FlexLevelStatus,
            FlexLastLevel: rCurrent?.RegistrationInfo.FlexLastLevel,
            UnlimitedLiveCoachingDate: rCurrent?.RegistrationInfo.UnlimitedLiveCoachingDate,
            ElectiveRequired: isFlex ? this.ElectivesFlag && rCurrent?.RegistrationInfo.ElectiveRequired : undefined,
            FoundationMaterialId: isFlex
              ? this.ElectivesFlag && rCurrent?.RegistrationInfo.Materials.find((mat) => !mat.IsElective)?.LPID
              : undefined,
            ElectiveMaterials: isFlex
              ? this.ElectivesFlag && rCurrent?.RegistrationInfo.Materials.filter((mat) => mat.IsElective)
              : [],
            IsCore: !(curr.IsElective && curr.IsTest),
            TestMaterials:
              !isFlex && !curr.IsTest && !curr.IsElective && curr.HasTest
                ? rCurrent?.RegistrationInfo.Materials.filter(({ IsTest }) => IsTest)
                : undefined,
            FromRegistration: {
              ...rCurrent?.RegistrationInfo,
            },
            RegistrationReferences,
            MaterialLevel: extractNumbeFromStr(curr.MaterialName) || 0,
          }
          return { ...mAcc, ...res }
        }, {})
        pAcc[pCurrent.ProgramId] = {
          materials,
          programName: pCurrent.ProgramName,
          programType: pCurrent.ProgramType,
          programId: pCurrent.ProgramId,
        }
        return pAcc
      }, {})
      rAcc[currRegistrationId] = {
        programs,
        displayName: rCurrent.RegistrationInfo.DisplayName,
        status: rCurrent.RegistrationInfo.Status,
        registrationId: currRegistrationId,
        testStatus: rCurrent.RegistrationInfo.TestStatus,
      }
      return rAcc
    }, {} as RegistrationProgramMaterials)
    this.DropDownMaterials = this.buildDropDownMaterials()
    this.Materials = registrationWrappers?.reduce((acc, current) => {
      const isFlex = IsFlex(current.RegistrationInfo)
      if (current.RegistrationInfo.AtRisk && !this.AtRiskProgram) {
        this.AtRiskProgram = new PortalProgram(
          current.RegistrationInfo.Programs.filter((program) => !FINISHED_PROGRAM_TYPES.includes(program.Status))[0]
        )
      }
      this.hasMaterials = !!(this.hasMaterials || current?.RegistrationInfo?.Materials?.length)
      //const materials = current?.RegistrationInfo?.Materials?.filter(({ LPID }) => LPID).reduce((mAcc, curr) => {
      const materials = current?.RegistrationInfo?.Materials?.reduce((mAcc, curr) => {
        const res = {}
        const levelRegEx = /[0-9]{2}|[0-9]/ // material language level is currently derived from material name
        const [Registration] = this.getRegStatusByLPID(curr)
        const [ProgramEndDateReg] = this.getProgramEndDateByLPID(curr)
        const RegistrationReferences = student?.RegistrationWrappers.filter(({ RegistrationInfo }) =>
          RegistrationInfo.Materials.some(({ LPID }) => LPID === curr.LPID)
        ).map(({ RegistrationInfo }) => RegistrationInfo)
        res[curr.LPID] = {
          ...curr,
          ProgramEndDate: ProgramEndDateReg?.RegistrationInfo.ProgramEndDate,
          TestStatus: Registration?.RegistrationInfo?.TestStatus,
          partialName: curr.MaterialName?.split(levelRegEx)?.[0],
          languageLevel: current?.RegistrationInfo.LanguageLevel,
          language: current?.RegistrationInfo?.Language,
          ProgramType: current?.RegistrationInfo?.ProgramType ?? '',
          ProgramId: current?.RegistrationInfo?.Programs?.[0]?.ProgramId, // assumes 1 program per registration
          FlexVersion: current?.RegistrationInfo?.FlexVersion,
          FlexLevelStatus: current?.RegistrationInfo?.FlexLevelStatus,
          FlexLastLevel: current?.RegistrationInfo.FlexLastLevel,
          UnlimitedLiveCoachingDate: current.RegistrationInfo.UnlimitedLiveCoachingDate,
          ElectiveRequired: isFlex ? this.ElectivesFlag && current.RegistrationInfo.ElectiveRequired : undefined,
          FoundationMaterialId: isFlex
            ? this.ElectivesFlag && current.RegistrationInfo.Materials.find((mat) => !mat.IsElective)?.LPID
            : undefined,
          ElectiveMaterials: isFlex
            ? this.ElectivesFlag && current.RegistrationInfo.Materials.filter((mat) => mat.IsElective)
            : [],
          IsCore: !isFlex
            ? this.MaterialsForDD?.[curr.LPID] && this.MaterialsForDD?.[curr.LPID]?.suboptions?.length
            : undefined,
          TestMaterials:
            !isFlex && !curr.IsTest && !curr.IsElective && curr.HasTest
              ? Registration?.RegistrationInfo.Materials.filter(({ IsTest }) => IsTest)
              : undefined,
          FromRegistration: {
            ...Registration?.RegistrationInfo,
          },
          RegistrationReferences,
          RegistrationReference: current?.RegistrationInfo,
          MaterialLevel: extractNumbeFromStr(curr.MaterialName) || 0,
        }
        return { ...mAcc, ...res }
      }, {})
      return { ...acc, ...materials }
    }, {})
    this.Programs = (student?.RegistrationWrappers || [])?.reduce((acc, current) => {
      const programs = current?.RegistrationInfo?.Programs?.reduce((pAcc, data) => {
        const res = {}
        res[data.ProgramId] = new PortalProgram(
          {
            ...{ ...data, FromRegistration: current.RegistrationInfo },
            FlexLevelStatus: current?.RegistrationInfo?.FlexLevelStatus,
          },
          this.Timezone
        )
        return { ...pAcc, ...res }
      }, {})
      return { ...acc, ...programs }
    }, {})
    if (student) {
      const registration = this.getProgramEndDateByLPID({ LPID: activeMaterial } as RegistrationMaterial)[0]
      this.OPT = new OnlinePlacementTest(student, registration)
    }
  }

  getUserIdByProgramId(id: string) {
    const p = this.Programs?.[id]
    return p?.isProgramF2FStar() ? this.student?.RecordId2B : this.student?.RecordId
  }

  /**
   * Get the correct student ID (SFId or SFId2B) based on the learning path IDs.
   * If any of the LPIDs belong to F2F-STAR (2B) program, return SFId2B, otherwise return SFId.
   * @param lpIds - Array of learning path IDs to check
   * @returns The correct student ID to use for API calls
   */
  getCorrectStudentIdByLpIds(lpIds: string[]): string {
    const sfId2B = this.student?.RecordId2B || this.student?.Student2B
    const sfId = this.student?.RecordId

    // If no SFId2B available, return SFId
    if (!sfId2B) {
      return sfId
    }

    // Check if any of the requested LPIDs belong to F2F-STAR (2B) program
    for (const lpId of lpIds) {
      const material = this.Materials?.[lpId]
      if (material?.ProgramType && PROGRAM_TYPES.F2F_STAR.includes(material.ProgramType)) {
        return sfId2B
      }
    }

    return sfId
  }

  buildDropDownMaterials(): MaterialsMapping {
    let materialsMapping: MaterialsMapping = {}

    if (!this.RegistrationProgramMaterials) return materialsMapping

    Object.keys(this.RegistrationProgramMaterials).forEach((registrationId) => {
      const registration = this.RegistrationProgramMaterials[registrationId]
      const programs = registration?.programs

      if (programs) {
        Object.keys(programs).forEach((programId) => {
          const program = programs[programId]
          const materials = program?.materials

          if (materials) {
            Object.keys(materials).forEach((lpid) => {
              const material = materials[lpid]
              const key = `${registrationId}|${programId}|${lpid}`
              material['UniqueKey'] = key
              materialsMapping[key] = material
            })
          }
        })
      }
    })
    return materialsMapping
  }

  hasSchedInstructorsTab() {
    return (
      this.RegistrationPortalPrograms.studentProgramsSortedDropdown(() => moment.tz(moment(), this.Timezone)).length > 0
    )
  }
  hasEventsTab() {
    return (this.HasOnDemand && this.EventsBoDFlag) || this.RegistrationPortalPrograms.EventsPrograms.length > 0
  }
  AYCLSequence(currentMaterial: RegistrationMaterial) {
    if (currentMaterial.FlexVersion === FLEX_VERSION.SINGLE) {
      return currentMaterial.MaterialName
    }
    const collator = new Intl.Collator(undefined, { numeric: true, sensitivity: 'base' })
    const sequence = Object.keys(this.Materials)
      .filter((key) => {
        return (
          (IsFlex(this.Materials[key]) &&
            this.Materials[key].language === currentMaterial.language &&
            this.Materials[key].FlexVersion === currentMaterial.FlexVersion &&
            !this.Materials[key].IsElective &&
            !this.Materials[key].IsTest) ||
          (!IsFlex(this.Materials[key]) && !this.Materials[key].IsElective && !this.Materials[key].IsTest)
        )
      })
      .map((key) => this.Materials[key].MaterialName)
      .filter((materialName) => !/MyBerlitz.*Tutor/.test(materialName))
      .sort(collator.compare)
    return sequence.length > 1 ? sequence[0] + ' - ' + sequence[sequence.length - 1] : sequence[0]
  }
  simpleFormatMaterialDropdown = (materials: RegistrationMaterial[] = []) =>
    materials.map((material) => ({
      label: material.MaterialName,
      value: material.LPID || `{${NOLPID}-${material.RecordId}}`,
      data: this.Programs?.[material.ProgramId],
      material: this.Materials[material.LPID],
      regStatus: undefined,
      sortValue: 0,
      suboptions: [],
    }))
  formatMaterialDropdown = (materials: RegistrationMaterial[] = []) =>
    materials.map((material) => ({
      label: material.MaterialName,
      value: material.LPID || `{${NOLPID}-${material.RecordId}}`,
      data: this.Programs?.[material.ProgramId],
      material: this.Materials[material.LPID],
      regStatus: material.regStatus,
      sortValue: 0,
      suboptions: this.simpleFormatMaterialDropdown(material.suboptions),
    }))
  simpleFormatMaterialDropdown2 = (materials: RegistrationMaterial[] = []) =>
    materials.map((material) => ({
      label: material.MaterialName,
      value: material.UniqueKey || `{${NOLPID}-${material.RecordId}}`,
      data: this.Programs?.[material.ProgramId],
      material: this.DropDownMaterials[material.UniqueKey ?? ''],
      regStatus: undefined,
      sortValue: 0,
      suboptions: [],
    }))
  formatMaterialDropdown2 = (materials: RegistrationMaterial[] = []) =>
    materials.map((material) => ({
      label: material.MaterialName,
      value: material.UniqueKey || `{${NOLPID}-${material.RecordId}}`,
      data: this.Programs?.[material.ProgramId],
      material: this.DropDownMaterials[material.UniqueKey ?? ''],
      regStatus: material.regStatus,
      sortValue: 0,
      suboptions: this.simpleFormatMaterialDropdown2(material.suboptions),
    }))
  sortedMaterials(materialList?: MaterialsMapping) {
    // Convert mapping to array of materials; handle undefined or empty mappings
    if (!materialList) return []
    const materialsArray = Object.values(materialList)
    if (materialsArray.length === 0) return []

    const collator = new Intl.Collator(undefined, { numeric: true, sensitivity: 'base' })

    // Define status priority
    const statusPriority = {
      [REG_STATUS.ACTIVE]: 1,
      [REG_STATUS.PENDING_START]: 2,
      [REG_STATUS.COMPLETED]: 3,
      [REG_STATUS.EXPIRED]: 4,
    }

    // Sort the flat list directly
    const sorted = materialsArray.slice().sort((a, b) => {
      const aStatus = a?.regStatus || ''
      const bStatus = b?.regStatus || ''

      const aDate = a?.ProgramStartDate ? new Date(a.ProgramStartDate).getTime() : 0
      const bDate = b?.ProgramStartDate ? new Date(b.ProgramStartDate).getTime() : 0

      // Sort by status priority (Active → Pending → Others)
      const statusDiff = (statusPriority[aStatus] || 99) - (statusPriority[bStatus] || 99)
      if (statusDiff !== 0) return statusDiff

      // If same status, sort by start date (newest first)
      if (aDate && bDate && aDate !== bDate) return bDate - aDate

      // Fallback to name/level sorting
      return (
        collator.compare(a?.partialName || '', b?.partialName || '') ||
        (b?.languageLevel || 0) - (a?.languageLevel || 0)
      )
    })
    return sorted
  }
  
  getActiveLPIdFromMaterialDropdown = () =>
    this.MaterialDropDown?.find(
      ({ material }) =>
        material?.ElectiveRequired === ELECTIVE.REQUIRED ||
        material?.ElectiveRequired === ELECTIVE.ASSIGNED ||
        (material?.ElectiveRequired === ELECTIVE.NOT_REQUIRED &&
          material?.FlexLevelStatus === FLEX_LEVEL_STATUS.LEVEL_STARTED &&
          !material?.ElectiveMaterials?.length)
    )?.value

  getActiveIdFromMaterialDropdown = () =>
    decodeDDUniqueKey(
      this.MaterialDropDown2?.find(
        ({ material }) =>
          material?.ElectiveRequired === ELECTIVE.REQUIRED ||
          material?.ElectiveRequired === ELECTIVE.ASSIGNED ||
          (material?.ElectiveRequired === ELECTIVE.NOT_REQUIRED &&
            material?.FlexLevelStatus === FLEX_LEVEL_STATUS.LEVEL_STARTED &&
            !material?.ElectiveMaterials?.length) ||
          PROGRAM_TYPES.F2F_STAR.includes(material?.MaterialType)
      )?.value
    )

  storeActiveDropdownLPId = (activeDropdownLPId: string) => {
    const materials = getStoredMaterials()
    materials[this?.SFId] = activeDropdownLPId
    localStorage.setItem(`portal-selected-material`, JSON.stringify(materials))
  }
  storeActiveDropdownIds = (
    activeDropdownRegistrationId: string,
    activeDropdownProgramId: string,
    activeDropdownLPId: string
  ) => {
    const materials = getStoredMaterials()
    const registrations = getStoredRegistrations()
    const programs = getStoredPrograms()
    registrations[this?.SFId] = activeDropdownRegistrationId
    programs[this?.SFId] = activeDropdownProgramId
    materials[this?.SFId] = activeDropdownLPId
    localStorage.setItem(`portal-selected-material`, JSON.stringify(materials))
    localStorage.setItem(`portal-selected-registration`, JSON.stringify(registrations))
    localStorage.setItem(`portal-selected-program`, JSON.stringify(programs))
  }
  /**
   * a helper function that will return the core material based on the id parameter.
   * note: this function will only return the LPID
   * if you want to have more options please consider to use LearningPath class
   * make sure to remove the industry specific materials
   * @param id
   * @returns
   */
  getCoreLPId = (id: string) => {
    return this.Materials[id]?.FromRegistration?.Materials?.filter(
      ({ IndustrySpecificContent, SkillsTutorContent }) => !IndustrySpecificContent && !SkillsTutorContent
    )?.[0]?.LPID
  }
  get MaterialDropDown() {
    return this.formatMaterialDropdown(
      this.sortedMaterials(this.Materials).filter(
        ({ IsElective, IsTest }) => !this.ElectivesFlag || (!IsElective && !IsTest)
      )
    ).filter(
      ({ material }) =>
        (!this.MyBerlitzTutorTabFlag || !material.IndustrySpecificContent) &&
        (!this.SkillsTutorTabFlag || !material.SkillsTutorContent)
    )
  }

  get MaterialDropDown2() {
    return this.formatMaterialDropdown2(
      this.sortedMaterials(this.DropDownMaterials).filter(
        ({ IsElective, IsTest }) => !this.ElectivesFlag || (!IsElective && !IsTest)
      )
    ).filter(
      ({ material }) =>
        (!this.MyBerlitzTutorTabFlag || !material.IndustrySpecificContent) &&
        (!this.SkillsTutorTabFlag || !material.SkillsTutorContent)
    )
  }

  get LPMaterialDropDown() {
    return this.formatMaterialDropdown(this.sortedMaterials(this.MaterialsForDD))
  }

  get LPMaterialDropDown2() {
    return this.formatMaterialDropdown2(this.sortedMaterials(this.MaterialsForDD2))
  }

  // lpMaterialDropdown for industry specific content
  get LPMaterialDropDownForISC() {
    const materialArr = Object.entries(this.Materials)
      .map(([, val]) => val)
      .filter(({ IndustrySpecificContent }) => !!IndustrySpecificContent)
    return this.formatMaterialDropdown(materialArr)
  }
  // lpMaterialDropdown for industry specific content
  get LPMaterialsForISC() {
    const materialArr = Object.entries(this.Materials)
      .map(([, val]) => val)
      .filter(({ IndustrySpecificContent }) => !!IndustrySpecificContent)
    return materialArr
  }
  get MaterialsISC() {
    return Object.entries(this.Materials)
      .filter(([, value]) => !!value.IndustrySpecificContent)
      .reduce((p, [k, v]) => {
        p[k] = v
        return p
      }, {}) as IDictionary<RegistrationMaterial>
  }
  // lpMaterialDropdown for skills tutor content
  get LPMaterialDropDownForSTC() {
    const materialArr = Object.entries(this.Materials)
      .map(([, val]) => val)
      .filter(({ SkillsTutorContent }) => !!SkillsTutorContent)
    return this.formatMaterialDropdown(materialArr)
  }
  // lpMaterialDropdown for skills tutor content
  get LPMaterialsForSTC() {
    const materialArr = Object.entries(this.Materials)
      .map(([, val]) => val)
      .filter(({ SkillsTutorContent }) => !!SkillsTutorContent)
    return materialArr
  }
  get MaterialsSTC() {
    // console.log('materials from student profile ', this.Materials)
    return Object.entries(this.Materials)
      .filter(([, value]) => !!value.SkillsTutorContent)
      .reduce((p, [k, v]) => {
        p[k] = v
        return p
      }, {}) as IDictionary<RegistrationMaterial>
  }
  // returns non Industry specific materials as a lookup
  get MaterialsNonISC() {
    return (
      Object.entries(this.Materials)
        // remove entries that has IndustrySpecificContent if MyBerlitzTutorTabFlag in ON
        // return eveything is MyBerlitzTutorTabFlag is off
        .filter(([, value]) => !this.MyBerlitzTutorTabFlag || !value.IndustrySpecificContent)
        .reduce((p, [k, v]) => {
          p[k] = v
          return p
        }, {})
    )
  }
  get EncodedDistinctISCNames() {
    return uniqBy(this.LPMaterialsForISC, 'IndustrySpecificContent').map(({ IndustrySpecificContent }) =>
      btoa(IndustrySpecificContent || '')
    )
  }
  get EncodedDistinctPartialNames() {
    return uniqBy(this.LPMaterialsForISC, 'partialName').map(({ partialName }) => btoa(partialName || ''))
  }
  get EncodedDistinctSTCNames() {
    // if (this.LPMaterialsForSTC.length === 0) {
    //   // Fallback: return base64 of a constant string
    //   return [btoa('SkillTutorTab')]
    // }
    // return uniqBy(this.LPMaterialsForSTC, 'SkillsTutorContent').map(
    //   ({ SkillsTutorContent }) => btoa(SkillsTutorContent || '') || 'Dzrysy'
    // )

    return [btoa('SkillsTutorTab')]
  }
  get SearchMaterialDropDown() {
    return this.formatMaterialDropdown(this.sortedMaterials(this.Materials))
  }
  get CTMaterials() {
    return this.CT
  }
  get IsSpeakingTutorOptOut() {
    return !!this.student?.ContactInfos?.Account?.ToggleSpeakingTutorOptOut
  }
  get IsSkillsTutorOptOut() {
    return !!this.student?.ContactInfos?.Account?.ToggleSkillsTutorOptOut
  }
  get IsAllAssignedSkillMaterialsCompleted() {
    return Object.values(this.MaterialsSTC || {}).every((material) => material.SkillsTutorCompleted === true)
  }
  get HasElectiveMaterial() {
    return (
      this.ElectivesFlag &&
      this.student.RegistrationWrappers.some(({ RegistrationInfo: { ElectiveRequired = '' } }) =>
        [ELECTIVE.REQUIRED, ELECTIVE.ASSIGNED].includes(ElectiveRequired)
      )
    )
  }
  get PortalPrograms() {
    return new PortalPrograms(this.student?.ProgramStudentWrappers ?? [])
  }
  get RegistrationPortalPrograms() {
    const programs = this.Programs
      ? Object.keys(this.Programs).map((id) => ({
          ProgramStudentInfo: { ...this.Programs[id].program },
        }))
      : []
    return new PortalPrograms(programs as ProgramStudent[])
  }
  get LanguageLearning() {
    return this.RegistrationPortalPrograms.ProgramLanguages
  }
  get ProgramTypes() {
    return this.RegistrationPortalPrograms.ProgramTypes
  }
  get LessonsTaken() {
    return this.RegistrationPortalPrograms.LessonsTaken
  }
  get LastLessonDates() {
    return this.RegistrationPortalPrograms.LastLessonDates
  }
  get StartDates() {
    return this.RegistrationPortalPrograms.StartDates
  }
  get Status() {
    return this.RegistrationPortalPrograms.Status
  }
  get LessonRemainings() {
    return this.RegistrationPortalPrograms.LessonRemainings
  }
  get VirtualClassRoomProviders() {
    return this.RegistrationPortalPrograms.VirtualClassRoomProviders
  }
  get ContractEndDate() {
    return this.RegistrationPortalPrograms.ContractEndDate
  }
  get SingleSessionSchedulings() {
    return this.RegistrationPortalPrograms.SingleSessionSchedulings
  }
  get GroupConversationClasses() {
    return this.RegistrationPortalPrograms.GroupConversationClasses
  }
  get AccountNames() {
    return this.uniqString(
      this.student.RegistrationWrappers.map(({ RegistrationInfo }) => RegistrationInfo.AccountName)
    )
  }
  get LearningCenterNames() {
    return this.uniqString(
      this.student.RegistrationWrappers.map(({ RegistrationInfo }) => RegistrationInfo.LearningCenterName)
    )
  }
  get TotalAchievedAttendancePercentes() {
    return this.uniqString(
      this.student.RegistrationWrappers.map(({ RegistrationInfo }) => RegistrationInfo.TotalAchievedAttendancePercent)
    )
  }
  get FlexVersions() {
    return this.uniqString(
      this.student.RegistrationWrappers.map(({ RegistrationInfo }) => RegistrationInfo.FlexVersion)
    )
  }
  uniqString = (data?: (string | number | undefined)[]) => uniq(data).filter(Boolean).join()
  get Levels() {
    return this.RegistrationPortalPrograms.ProgramLevels
  }
  get ActiveLevels() {
    return this.RegistrationPortalPrograms.ActiveProgramLevels
  }
  get ProductTypes() {
    return this.RegistrationPortalPrograms.ProductTypes
  }
  get SFId() {
    return this.student?.RecordId
  }
  get SFId2B() {
    return this.student?.Student2B
  }
  get is2bStudentOnly() {
    const sfId = this.student?.RecordId
    const sfId2B = this.student?.Student2B
    return sfId && sfId2B && sfId === sfId2B
  }
  get has2Aand2BSubscriptions() {
    const sfId = this.student?.RecordId
    const sfId2B = this.student?.Student2B
    return sfId && sfId2B && sfId !== sfId2B
  }
  get onlyF2fStarPrograms() {
    return (
      (this.PortalPrograms?.Programs?.length || 0) > 0 &&
      (this.PortalPrograms?.Programs as any[])?.every((p) => p?.isProgramF2FStar?.() ?? false)
    )
  }
  get has2BOrF2fStarProgramsOnly() {
    return this.is2bStudentOnly || this.onlyF2fStarPrograms
  }
  get Timezone() {
    return this.student?.PreferredTimezone
  }
  get Groups() {
    const wrappers = (this.student?.ProgramStudentWrappers ?? []) as ProgramStudent[]
    return wrappers
      .map((program) => {
        const studentGrp = program.ProgramStudentInfo.studentGroupInfo || ({} as studentGroupInfo)
        studentGrp.Language = program.ProgramStudentInfo?.Language
        studentGrp.ProgramId = program.ProgramStudentInfo?.ProgramId
        return studentGrp.Id ? studentGrp : null
      })
      .filter(Boolean)
  }
  get HasOnDemand() {
    return Object.keys(this.Materials || {})?.some((i) => ON_DEMAND.includes(this.Materials[i].ProgramType || ''))
  }
  get OnlyOnDemand() {
    const types = Object.keys(this.Materials || {})?.map((i) => this.Materials[i].ProgramType)
    const t = [...new Set(types)]
    if (t.length === 1 && t.includes(ON_DEMAND[0])) {
      return true
    }
    // only on demand
    return false
  }
  get AtRiskPrograms() {
    return this.student?.RegistrationWrappers.filter(({ RegistrationInfo }) => RegistrationInfo.AtRisk).reduce(
      (p: PortalProgram[], { RegistrationInfo }) =>
        p.concat(
          RegistrationInfo.Programs.filter((program) => !FINISHED_PROGRAM_TYPES.includes(program.Status)).map(
            (program) => new PortalProgram(program)
          )
        ),
      []
    )
  }
  get MyBerlitzEnv() {
    return this.student?.MyBerlitzEnvironment ?? MY_BERLITZ_ENV.DEFAULT
  }
  get HasBoD1on1Registration() {
    return this.student?.RegistrationWrappers?.some(({ RegistrationInfo }) => {
      return RegistrationInfo.Programs?.some(({ ProgramType }) => {
        return PROGRAM_TYPES.ON_DEMAND_1.includes(ProgramType)
      })
    })
  }
  getCurriculumMaterials(learningPaths, currentLP = '') {
    const currentMaterial = this.Materials[currentLP]
    // lets filter CT from reg wrapper and only use it when it has a match in LMS API
    const filteredForCT = (this.CT?.filter(({ LPID }) => learningPaths.find(({ lpid }) => lpid === LPID)) || []).filter(
      (item) => (currentMaterial ? currentMaterial.RegistrationId === item.RegistrationId : true)
    )

    // Separate current LP items from others
    const currentLPItems = filteredForCT.filter((item) => item.LPID === currentLP)
    const otherLPItems = filteredForCT.filter((item) => item.LPID !== currentLP)

    // Combine with current LP first, then others
    const reorderedForCT = [...currentLPItems, ...otherLPItems]

    // Lets check registration materials if it
    // has data from learning path response
    // this will also fix the order of learning path
    const validatedLP = reorderedForCT.reduce((acc, item) => {
      const unit = learningPaths.find(({ lpid }) => lpid === item.LPID)
      if (unit) {
        acc = acc.concat(unit)
      }
      return acc
    }, [] as any)

    const seenIntroLPIDs = new Set<String>()
    const curriculumMaterials: CurriculumMaterial[] = validatedLP.reduce((acc, item) => {
      const unit = reorderedForCT.find(({ LPID }) => LPID === item.lpid)
      if (unit) {
        const nUnit: CurriculumMaterial[] = item.details.units
          .map((item: any) => {
            const activity = item.activities.find(({ type }) => type === 'p20')
            return {
              ...unit,
              MaterialName: unit.MaterialName,
              CMID: parseInt(getCMID(activity?.url), 10),
              IsIntro:
                item.fullname.includes('Lesson - Introduction and Welcome') || item.shortname.includes('Unit 00 - CT'),
            }
          })
          .filter((item: CurriculumMaterial) => {
            if (item.IsIntro) {
              if (seenIntroLPIDs.has(item.LPID)) {
                return false
              }
              seenIntroLPIDs.add(item.LPID)
              return true
            }
            return item
          })
        acc = acc.concat(...nUnit)
      }
      return acc
    }, [] as CurriculumMaterial[])
    return curriculumMaterials
  }
  get HasMaterialButNoLPID() {
    return Object.keys(this.Materials || {}).length === 0 && this.hasMaterials
  }
  /**
   * Lets arrange registrations by material
   * so that we have the ability to get test unlocked later on
   */
  private getRegistrationsByCoreMaterialId() {
    let registrationsByCoreMaterialId = new Map<string, RegistrationWrapper[]>()
    /**
     * There are scenarios that Registration has no materials []
     * We need to check if that registration is an OPT
     * if it is then we need to add a placeholder material
     */
    const registrationWrappers = (this.student?.RegistrationWrappers ?? []).map((registration) =>
      OnlinePlacementTest.generatePlaceholder(registration)
    )
    registrationsByCoreMaterialId = registrationWrappers?.reduce((acc, current) => {
      const regMaterials = current?.RegistrationInfo?.Materials
      if (regMaterials.length > 1) {
        regMaterials.forEach((material) => {
          if (material) {
            if (!acc.has(material.LPID)) {
              acc.set(material.LPID, [])
            }
            acc.get(material.LPID)?.push(current)
          }
        })
      } else if (regMaterials.length) {
        let material: RegistrationMaterial | undefined = regMaterials[0]
        if (material) {
          if (!acc.has(material.LPID)) {
            acc.set(material.LPID, [])
          }
          acc.get(material.LPID)?.push(current)
        }
      }
      return acc
    }, registrationsByCoreMaterialId)
    return registrationsByCoreMaterialId
  }
  /**
   * This function will help you to get the proper test status.
   */
  private getRegStatusByLPID(currentMaterial: RegistrationMaterial): [RegistrationWrapper | undefined] {
    let currentItem: RegistrationWrapper | undefined
    /**
     * Lets arrange registrations by material
     * so that we have the ability to get test unlocked later on
     */
    let registrationsByCoreMaterialId = this.getRegistrationsByCoreMaterialId()
    if (registrationsByCoreMaterialId) {
      /**
       * find the registration that matches the current material
       */
      const registrations = registrationsByCoreMaterialId.get(currentMaterial.LPID) ?? []
      /**
       * Lets check if the registrations from this current material
       * has test unlocked
       */
      const testUnlockRegs = registrations.filter(
        (registration) => registration.RegistrationInfo.TestStatus === CURRICULUM_TEST_STATUS.UNLOCKED
      )
      /**
       * If it has test unlocked, then use the last item (recent)otherwise use the whatever last registration we have
       */
      if (testUnlockRegs.length) {
        currentItem = last(testUnlockRegs)
      } else {
        currentItem = last(registrations)
      }
    }
    return [currentItem]
  }
  /**
   * This function will help you to get the proper program end date.
   */
  private getProgramEndDateByLPID(currentMaterial: RegistrationMaterial): [RegistrationWrapper | undefined] {
    let currentItem: RegistrationWrapper | undefined
    /**
     * Lets arrange registrations by material
     * so that we have the ability to get test unlocked later on
     */
    let registrationsByCoreMaterialId = this.getRegistrationsByCoreMaterialId()
    if (registrationsByCoreMaterialId) {
      /**
       * find the registration that matches the current material
       */
      const registrations = registrationsByCoreMaterialId.get(currentMaterial.LPID) ?? []
      /**
       * Lets get the latest registration's ProgramEndDate from this current material
       */
      currentItem = orderBy(registrations, ['RegistrationInfo.ProgramEndDate'], ['desc'])[0]
    }
    return [currentItem]
  }
  /**
   * Build material dropdown per registration
   * @returns MaterialsMapping | undefined
   */
  // @ts-ignore
  private buildDropdown2() {
    let materialsForDD: MaterialsMapping | undefined
    /**
     * There are scenarios that Registration has no materials []
     * We need to check if that registration is an OPT
     * if it is then we need to add a placeholder material
     */
    const registrationWrappers = (this.student?.RegistrationWrappers ?? []).map((registration) =>
      OnlinePlacementTest.generatePlaceholder(registration)
    )
    /**
     * Lets group registrations by registration id
     * so that we have the ability to get test unlocked later on
     */
    let registrationsByRegistrationId = new Map<string, RegistrationWrapper[]>()
    registrationsByRegistrationId = registrationWrappers?.reduce((acc, current) => {
      if (current?.RegistrationInfo) {
        if (!acc.has(current?.RegistrationInfo?.RegistrationId)) {
          acc.set(current?.RegistrationInfo?.RegistrationId, [])
        }
        acc.get(current?.RegistrationInfo?.RegistrationId)?.push(current)
      }
      return acc
    }, registrationsByRegistrationId)
    let dropDownByCoreMaterialId = new Map<string, RegistrationMaterial>()
    if (registrationsByRegistrationId) {
      materialsForDD = {}
      const levelRegEx = /[0-9]{2}|[0-9]/ // material language level is currently derived from material name
      for (let [registrationId, registrations] of registrationsByRegistrationId) {
        /**
         * Lets check if the registrations from these core material
         * has test unlocked
         */
        const registrationsWithTestUnlocked = registrations.filter(
          (registration) => registration.RegistrationInfo.TestStatus === CURRICULUM_TEST_STATUS.UNLOCKED
        )
        /**
         * If it has test unlocked, then use the last item otherwise use the whatever last registration we have
         */
        const current = registrationsWithTestUnlocked.length ? last(registrationsWithTestUnlocked) : last(registrations)
        // if the industry specific content or skills tutor content ff is on, filter the main course lp dd to show non ISC materials
        const regMaterials = (current?.RegistrationInfo?.Materials ?? []).filter(
          ({ IndustrySpecificContent, SkillsTutorContent }) =>
            (!this.MyBerlitzTutorTabFlag || !IndustrySpecificContent) &&
            (!this.SkillsTutorTabFlag || !SkillsTutorContent)
        )
        const testMaterial = regMaterials.find((material) => material.IsTest)
        let coreMaterial: RegistrationMaterial | undefined =
          regMaterials.length > 1
            ? regMaterials.find((material) => !material.IsElective && !material.IsTest) || regMaterials[0]
            : regMaterials[0]
        /**
         * For Flex and BoD:
         *  - If the assigned value to coreMaterial is either elective or CT then we need to set it to undefined
         * For Live online:
         *  - If the assigned value to coreMaterial is elective or test then we don't need to set it to undefined
         */
        const program = this.getProgramByRegistration(current)
        if (!program.isProgramLiveOnline) {
          if (coreMaterial?.IsElective || coreMaterial?.IsTest) {
            if (!regMaterials[0]?.IsElective || !regMaterials[0]?.IsTest) {
              coreMaterial = undefined
            }
          }
        }
        const dd = {
          ...coreMaterial,
          ProgramStartDate: program?.ProgramEnrollDate,
          partialName: coreMaterial?.MaterialName?.split(levelRegEx)?.[0],
          languageLevel: Number((coreMaterial?.MaterialName?.match(levelRegEx) || [])[0] || 0),
          language: current?.RegistrationInfo?.Language,
          ProgramType: current?.RegistrationInfo?.ProgramType ?? '',
          suboptions: [] as any,
          testStatus: current?.RegistrationInfo?.TestStatus,
          regStatus: current?.RegistrationInfo?.Status,
        }
        const nonCTmaterials = regMaterials.filter((material) => !material.IsTest)
        /**
         * Finally lets build the dropdown
         * Lets add first non CT materials (core material, elective)
         */
        dd.suboptions = nonCTmaterials.map((curr) => {
          return {
            ...curr,
            partialName: curr.MaterialName?.split(levelRegEx)?.[0],
            languageLevel: Number((curr?.MaterialName?.match(levelRegEx) || [])[0] || 0),
            language: current?.RegistrationInfo?.Language,
            testStatus: current?.RegistrationInfo.TestStatus,
          }
        })
        /**
         * If there is no non CT materials then we don't display CT materials
         * since CT is tied to either core or elective materials
         */
        if (testMaterial && dd.suboptions.length > 0) {
          const languageLevel = current?.RegistrationInfo?.LanguageLevel || ''
          const language = current?.RegistrationInfo?.Language || ''
          const MaterialName = `Curriculum Test ${language} ${languageLevel}`
          /**
           * Lets add CT materials here
           */
          dd.suboptions?.push({
            ...testMaterial,
            partialName: MaterialName,
            MaterialName,
            languageLevel,
            language,
          })
        }
        // Move the core material to the first position
        moveToFirstPosition(dd.suboptions, dd.LPID, 'LPID')
        dropDownByCoreMaterialId.set(registrationId, dd)
      }
    }
    /**
     * Convert Map to plain Object
     */
    for (const [key, value] of dropDownByCoreMaterialId) {
      materialsForDD![key] = value
    }
    return materialsForDD
  }

  private buildDropdown3(): MaterialsMapping | undefined {
    let materialsForDD: MaterialsMapping | undefined

    const registrationWrappers = (this.student?.RegistrationWrappers ?? []).map((registration) =>
      OnlinePlacementTest.generatePlaceholder(registration)
    )

    let registrationsByRegistrationId = new Map<string, RegistrationWrapper | 'AMBIGUOUS'>()
    registrationsByRegistrationId = registrationWrappers?.reduce((acc, current) => {
      if (current?.RegistrationInfo) {
        if (!acc.has(current?.RegistrationInfo?.RegistrationId)) {
          acc.set(current?.RegistrationInfo?.RegistrationId, current)
        } else {
          acc.set(current?.RegistrationInfo?.RegistrationId, 'AMBIGUOUS') // If multiple registrations exist against the same ID
        }
      }
      return acc
    }, registrationsByRegistrationId)

    let dropDownByCoreMaterialId = new Map<string, RegistrationMaterial>()

    if (registrationsByRegistrationId) {
      materialsForDD = {}
      const levelRegEx = /[0-9]{2}|[0-9]/ // material language level is currently derived from material name

      for (let [registrationId, current] of registrationsByRegistrationId) {
        if (current === 'AMBIGUOUS' || !current.RegistrationInfo.Materials.length) continue // Skip ambiguous registration or empty materials

        // if the industry specific content or skills tutor content ff is on, filter the main course lp dd to show non ISC materials
        const regMaterials = (current?.RegistrationInfo?.Materials ?? []).filter(
          ({ IndustrySpecificContent, SkillsTutorContent }) =>
            (!this.MyBerlitzTutorTabFlag || !IndustrySpecificContent) &&
            (!this.SkillsTutorTabFlag || !SkillsTutorContent)
        )
        let coreMaterial: RegistrationMaterial | undefined =
          regMaterials.length > 1
            ? regMaterials.find((material) => !material.IsElective && !material.IsTest) || regMaterials[0]
            : regMaterials[0]

        /**
         * For Flex and BoD:
         *  - If the assigned value to coreMaterial is either elective or CT then we need to set it to undefined
         * For Live online:
         *  - If the assigned value to coreMaterial is elective or test then we don't need to set it to undefined
         */
        const program = this.getProgramByRegistration(current)
        if (!program.isProgramLiveOnline) {
          if (coreMaterial?.IsElective || coreMaterial?.IsTest) {
            if (!regMaterials[0]?.IsElective || !regMaterials[0]?.IsTest) {
              coreMaterial = undefined
            }
          }
        }

        const testMaterial = regMaterials.find(
          (material) => material.LPID == coreMaterial?.MaterialAndTests?.[0]?.TestMaterialLPID
        )

        const coreMaterialUniqueKey = `${current?.RegistrationInfo?.RegistrationId}|${program?.ProgramId}|${coreMaterial?.LPID}`
        const testMaterialUniqueKey = `${current?.RegistrationInfo?.RegistrationId}|${program?.ProgramId}|${testMaterial?.LPID}`

        const dd = {
          ...coreMaterial,
          partialName: coreMaterial?.MaterialName?.split(levelRegEx)?.[0],
          languageLevel: Number((coreMaterial?.MaterialName?.match(levelRegEx) || [])[0] || 0),
          language: current?.RegistrationInfo?.Language,
          ProgramType: current?.RegistrationInfo?.ProgramType ?? '',
          suboptions: [] as any,
          testStatus: current?.RegistrationInfo?.TestStatus,
          regStatus: current?.RegistrationInfo?.Status,
          UniqueKey: coreMaterialUniqueKey,
        }
        const nonCTmaterials = regMaterials.filter((material) => !material.IsTest)
        /**
         * Finally lets build the dropdown
         * Lets add first non CT materials (core material, elective)
         */
        dd.suboptions = nonCTmaterials.map((curr) => {
          const UniqueKey = `${current?.RegistrationInfo?.RegistrationId}|${program?.ProgramId}|${curr?.LPID}`
          return {
            ...curr,
            partialName: curr.MaterialName?.split(levelRegEx)?.[0],
            languageLevel: Number((curr?.MaterialName?.match(levelRegEx) || [])[0] || 0),
            language: current?.RegistrationInfo?.Language,
            testStatus: current?.RegistrationInfo.TestStatus,
            UniqueKey,
          }
        })
        /**
         * If there is no non CT materials then we don't display CT materials
         * since CT is tied to either core or elective materials
         */
        if (testMaterial && dd.suboptions.length > 0) {
          const languageLevel = current?.RegistrationInfo?.LanguageLevel || ''
          const language = current?.RegistrationInfo?.Language || ''
          const MaterialName = `Curriculum Test ${language} ${languageLevel}`
          /**
           * Lets add CT materials here
           */
          dd.suboptions?.push({
            ...testMaterial,
            partialName: MaterialName,
            MaterialName,
            languageLevel,
            language,
            UniqueKey: testMaterialUniqueKey,
          })
        }
        // Move the core material to the first position
        moveToFirstPosition(dd.suboptions, dd.UniqueKey, 'UniqueKey')
        dropDownByCoreMaterialId.set(registrationId, dd)
      }
    }
    /**
     * Convert Map to plain Object
     */
    for (const [key, value] of dropDownByCoreMaterialId) {
      materialsForDD![key] = value
    }

    return materialsForDD
  }
  // @ts-ignore
  private buildDropDown() {
    let materialsForDD: MaterialsMapping | undefined
    /**
     * Lets arrange registrations by first item of the material
     * so that we have the ability to get test unlocked later on
     */
    let registrationsByCoreMaterialId = new Map<string, RegistrationWrapper[]>()
    registrationsByCoreMaterialId = this.student?.RegistrationWrappers?.reduce((acc, current) => {
      const regMaterials = current?.RegistrationInfo?.Materials
      /**
       * There are scenarios that Materials is []
       * so lets defined in TS that coreMaterial may get undefined value
       */
      let coreMaterial: RegistrationMaterial | undefined
      if (regMaterials.length > 1) {
        coreMaterial = regMaterials.find((material) => !material.IsElective && !material.IsTest) || regMaterials[0]
      } else if (regMaterials.length) {
        coreMaterial = regMaterials[0]
      }
      if (coreMaterial) {
        if (!acc.has(coreMaterial.LPID)) {
          acc.set(coreMaterial.LPID, [])
        }
        acc.get(coreMaterial.LPID)?.push(current)
      }
      return acc
    }, registrationsByCoreMaterialId)
    /**
     * General idea in here is that we are going to build dropdown list by core material id
     * that would show in these following order:
     * example
     * Curriculum Test English 1 (Core)
     * -- Meetings 1-5 level 1 (Elective)
     * -- Curriculum Test English 1 (CT)
     * The way we are going to build it is that we are going to check first
     * if the registrations contains test unlocked, if has,
     * then we are going to use it otherwise just whatever the last registration is
     */
    let dropDownByCoreMaterialId = new Map<string, RegistrationMaterial>()
    if (registrationsByCoreMaterialId) {
      materialsForDD = {}
      const levelRegEx = /[0-9]{2}|[0-9]/ // material language level is currently derived from material name
      for (let [coreMaterialId, registrations] of registrationsByCoreMaterialId) {
        /**
         * Lets check if the registrations from these core material
         * has test unlocked
         */
        const testUnlockRegs = registrations.filter(
          (registration) => registration.RegistrationInfo.TestStatus === CURRICULUM_TEST_STATUS.UNLOCKED
        )
        /**
         * If it has test unlocked, then use the last item (recent)otherwise use the whatever last registration we have
         */
        let current: RegistrationWrapper | undefined
        if (testUnlockRegs.length) {
          current = last(testUnlockRegs)
        }
        /**
         * If there is no test unlocked material
         */
        //const current = testUnlockRegs.length ? last(testUnlockRegs) : last(registrations)
        const regMaterials = current?.RegistrationInfo?.Materials ?? []
        const testMaterial = regMaterials.find((material) => material.IsTest)
        const coreMaterial =
          regMaterials.length > 1
            ? regMaterials.find((material) => !material.IsElective && !material.IsTest) || regMaterials[0]
            : regMaterials[0]
        const dd = {
          ...coreMaterial,
          partialName: coreMaterial?.MaterialName?.split(levelRegEx)?.[0],
          languageLevel: Number((coreMaterial?.MaterialName?.match(levelRegEx) || [])[0] || 0),
          language: current?.RegistrationInfo?.Language,
          ProgramType: current?.RegistrationInfo?.ProgramType ?? '',
          suboptions: [] as any,
          testStatus: current?.RegistrationInfo?.TestStatus,
        }
        const isFlex = IsFlex(dd)
        const nonCTmaterials = regMaterials.filter((material) => !material.IsTest)
        if (!isFlex) {
          /**
           * Finally lets build the dropdown
           * Lets add first non CT materials (core material, elective)
           */
          dd.suboptions = nonCTmaterials.map((curr) => {
            return {
              ...curr,
              partialName: curr.MaterialName?.split(levelRegEx)?.[0],
              languageLevel: Number((curr?.MaterialName?.match(levelRegEx) || [])[0] || 0),
              language: current?.RegistrationInfo?.Language,
              testStatus: current?.RegistrationInfo.TestStatus,
            }
          })
          if (testMaterial) {
            const languageLevel = current?.RegistrationInfo?.LanguageLevel || ''
            const language = current?.RegistrationInfo?.Language || ''
            const MaterialName = `Curriculum Test ${language} ${languageLevel}`
            /**
             * Lets add CT materials here
             */
            dd.suboptions?.push({
              ...testMaterial,
              partialName: MaterialName,
              MaterialName,
              languageLevel,
              language,
            })
          }
        }
        dropDownByCoreMaterialId.set(coreMaterialId, dd)
      }
    }
    /**
     * Convert Map to plain Object
     */
    for (const [key, value] of dropDownByCoreMaterialId) {
      materialsForDD![key] = value
    }
    return materialsForDD
  }
  getProgramByMaterialId = (id?: string) => {
    const programId = this?.Materials[id ?? '']?.ProgramId
    return this?.Programs[programId]
  }
  private getProgramByRegistration(reg: RegistrationWrapper | undefined) {
    const registration = this.student?.RegistrationWrappers?.find(
      (registration) => registration?.RegistrationInfo?.RegistrationId === reg?.RegistrationInfo?.RegistrationId
    )
    const programsByStudent = (registration?.RegistrationInfo?.Programs ?? []).map((program) => {
      const p: ProgramStudent = {
        ProgramStudentInfo: {
          ...program,
        },
      }
      return p
    })
    const programs = new PortalPrograms(programsByStudent)
    return programs.getProgram(reg?.RegistrationInfo?.Programs?.[0]?.ProgramId || '')
  }
  eventsSortedDropdown = (getDateWithUserTimezone?: any, nonFlex: boolean = false) => {
    const sorted = sortBy(
      this.student.RegistrationWrappers,
      (reg) => !VALID_PROGRAM_TYPES.map((type) => type.toLowerCase() === reg.RegistrationInfo.Status.toLowerCase()),
      (reg) =>
        moment(
          reg.RegistrationInfo?.Programs?.[0]?.ContractEndDate || reg.RegistrationInfo.ProgramEndDate,
          DATE_FORMAT.BASIC
        ).diff(getDateWithUserTimezone(), 'days') < 0
    )
      .filter(
        ({ RegistrationInfo }) => !RegistrationInfo?.Programs?.some(({ PortalNotNecessary }) => PortalNotNecessary)
      )
      .filter(({ RegistrationInfo }) => {
        // filter for https://berlitz.atlassian.net/browse/SCH-690
        let defaultFilter =
          RegistrationInfo.Status !== REG_STATUS.CANCELLED &&
          RegistrationInfo.Status !== REG_STATUS.COMPLETED &&
          RegistrationInfo.Status !== REG_STATUS.DRAFT &&
          !(
            getDateWithUserTimezone &&
            RegistrationInfo.Status === REG_STATUS.ACTIVE &&
            moment(
              RegistrationInfo?.Programs?.[0]?.ContractEndDate || RegistrationInfo.ProgramEndDate,
              DATE_FORMAT.BASIC
            ).diff(getDateWithUserTimezone(), 'days') < 0
          )
        // https://berlitz.atlassian.net/browse/SCH-817
        if (nonFlex && RegistrationInfo.Programs?.length) {
          return !new PortalProgram(RegistrationInfo?.Programs?.[0]).isFlexProgram() && defaultFilter
        }
        return defaultFilter
      })
      .filter(
        // filter for https://berlitz.atlassian.net/browse/SCH-740
        ({ RegistrationInfo }) =>
          !(
            [REG_STATUS.ACTIVE, REG_STATUS.PENDING_START].includes(RegistrationInfo.Status || '') &&
            RegistrationInfo?.Programs?.some(({ PortalNotNecessary }) => PortalNotNecessary)
          )
      )
      .filter(
        // filter for https://berlitz.atlassian.net/browse/PS-169
        ({ RegistrationInfo }) =>
          getDateWithUserTimezone()?.isBefore(
            RegistrationInfo?.Programs?.[0]?.ContractEndDate || RegistrationInfo.ProgramEndDate,
            'day'
          )
      )
    return sorted.map((reg) => {
      const program = reg.RegistrationInfo?.Programs?.[0]
      const {
        Language = reg.RegistrationInfo.Language,
        DeliveryMode,
        ProgramName = reg.RegistrationInfo.DisplayName,
        ProgramEnrollDate,
        Status = reg.RegistrationInfo.Status,
        ContractEndDate = reg.RegistrationInfo.ProgramEndDate,
        ProgramId,
      } = program || {}
      const DeliveryModeText = DeliveryMode ? `${DeliveryMode} - ` : ''
      const expired =
        getDateWithUserTimezone &&
        reg.RegistrationInfo.Status === REG_STATUS.ACTIVE &&
        moment(ContractEndDate, DATE_FORMAT.BASIC).diff(getDateWithUserTimezone(), 'days') < 0
          ? ' (Expired)'
          : ''
      return {
        label: `${Language} - ${DeliveryModeText}${ProgramName} - ${moment(ProgramEnrollDate).format(
          'MMMM DD, YYYY'
        )} - ${Status}${expired}`,
        value: ProgramId || reg.RegistrationInfo?.RegistrationId,
        data: program || reg.RegistrationInfo,
      }
    })
  }

  // Returns dropdown without F2F programs (for Events page)
  eventsSortedDropdownWithoutF2fStar = (getDateWithUserTimezone?: any, nonFlex: boolean = false) => {
    const allDropdown = this.eventsSortedDropdown(getDateWithUserTimezone, nonFlex)
    return allDropdown.filter(({ data }) => {
      const program = data
      return !new PortalProgram(program).isProgramF2FStar()
    })
  }

  getRegistrationByProgramId = (programId?: string) => {
    return this.student?.RegistrationWrappers.find(
      (registration) => registration?.RegistrationInfo?.Programs?.[0]?.ProgramId === programId
    )
  }

  getRegistrationById = (registrationId?: string) => {
    return this.student?.RegistrationWrappers.find(
      (registration) => registration?.RegistrationInfo?.RegistrationId == registrationId
    )
  }

  get hasActiveIndSpecificContent() {
    return Object.entries(this.Materials).some(
      ([, { IndustrySpecificContent, RegistrationReferences }]) =>
        !!IndustrySpecificContent &&
        RegistrationReferences.some(({ Status }) => [REG_STATUS.ACTIVE, REG_STATUS.PENDING_START].includes(Status))
    )
  }
  // get hasActiveSkillTutorContent() {
  //   return Object.entries(this.Materials).some(
  //     ([, { SkillTutorContent, RegistrationReferences }]) =>
  //       !!SkillTutorContent &&
  //       RegistrationReferences.some(({ Status }) => [REG_STATUS.ACTIVE, REG_STATUS.PENDING_START].includes(Status))
  //   )
  // }
}
export default PortalStudentProfile
